import React, { useEffect, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Box,
   Typography,
   IconButton,
   Alert,
   CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import CreateAlbumWizardPanels from './CreateAlbumWizardPanels';
import { useCreateAlbumWizard } from './useCreateAlbumWizard';

interface CreateAlbumDialogProps {
   open: boolean;
   onClose: () => void;
   onCreateAlbum: (albumData: {
      title: string;
      description?: string;
      type: 'private' | 'public' | 'paid';
      enableParticipantParticipation: boolean;
      pricePerPhoto?: number;
      pricingModel?: 'per_photo' | 'one_time' | 'donation';
      albumDate?: string;
      paidAlbumAccessType?: 'public' | 'private';
   }) => Promise<void>;
}

const CreateAlbumDialog: React.FC<CreateAlbumDialogProps> = ({ open, onClose, onCreateAlbum }) => {
   const theme = useTheme();
   const primaryDark = theme.palette.primary.dark;
   const wizard = useCreateAlbumWizard();
   const {
      activeStep,
      reset,
      handleNext,
      handleBack,
      buildPayload,
      setError,
      error,
      stepSubtitle,
   } = wizard;

   const [loading, setLoading] = useState(false);

   useEffect(() => {
      if (open) {
         reset();
      }
   }, [open, reset]);

   const handleClose = () => {
      reset();
      onClose();
   };

   const handlePrimary = async () => {
      if (activeStep < 2) {
         handleNext();
         return;
      }

      const { payload, error: buildErr } = buildPayload();
      if (buildErr || !payload) {
         setError(buildErr || 'Invalid album data');
         return;
      }

      setLoading(true);
      setError('');

      try {
         await onCreateAlbum({
            title: payload.title,
            description: payload.description,
            type: payload.type,
            enableParticipantParticipation: payload.enableParticipantParticipation,
            albumDate: payload.albumDate,
            ...(payload.type === 'paid' && {
               pricePerPhoto: payload.pricePerPhoto,
               pricingModel: payload.pricingModel,
               paidAlbumAccessType: payload.paidAlbumAccessType,
            }),
         });
         handleClose();
      } catch (err) {
         setError(err instanceof Error ? err.message : 'Failed to create album');
      } finally {
         setLoading(false);
      }
   };

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth='md'
         fullWidth
         PaperProps={{
            sx: (t) => ({
               borderRadius: 2,
               maxWidth: { xs: '95vw', sm: '700px', md: '800px' },
               margin: 'auto',
               minHeight: '500px',
               bgcolor: 'background.paper',
               border: `1px solid ${t.palette.divider}`,
               boxShadow: t.shadows[12],
            }),
         }}
      >
         <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {activeStep > 0 && (
                     <IconButton onClick={handleBack} size='small' sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                     </IconButton>
                  )}
                  <Box>
                     <Typography
                        variant='h5'
                        sx={{
                           fontFamily: theme.typography.h3.fontFamily,
                           fontWeight: 600,
                           color: 'text.primary',
                           letterSpacing: theme.typography.h3.letterSpacing,
                        }}
                     >
                        Create Album
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {`Step ${activeStep + 1} of 3 • ${stepSubtitle}`}
                     </Typography>
                  </Box>
               </Box>
               <IconButton onClick={handleClose} size='small' aria-label='Close'>
                  <CloseIcon />
               </IconButton>
            </Box>
         </DialogTitle>

         <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
            {error && (
               <Alert severity='error' sx={{ mb: 2 }}>
                  {error}
               </Alert>
            )}

            <CreateAlbumWizardPanels wizard={wizard} />
         </DialogContent>

         <DialogActions
            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, gap: 2, justifyContent: 'flex-end' }}
         >
            <Button
               onClick={activeStep === 0 ? handleClose : handleBack}
               sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                     bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                     color: 'primary.main',
                  },
               }}
            >
               {activeStep === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
               variant='contained'
               color='primary'
               onClick={handlePrimary}
               disabled={loading}
               startIcon={loading ? <CircularProgress size={20} color='inherit' /> : null}
               sx={{
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                  boxShadow: (t) => `0 4px 14px ${alpha(t.palette.primary.main, 0.35)}`,
                  '&:hover': {
                     bgcolor: primaryDark,
                     boxShadow: (t) => `0 6px 20px ${alpha(t.palette.primary.main, 0.45)}`,
                  },
                  '&:disabled': {
                     bgcolor: 'action.disabledBackground',
                     color: 'text.disabled',
                     boxShadow: 'none',
                  },
               }}
            >
               {loading ? 'Creating...' : activeStep < 2 ? 'Next' : 'Create Album'}
            </Button>
         </DialogActions>
      </Dialog>
   );
};

export default CreateAlbumDialog;
